import React, { useState, useEffect, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import { tr } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import {
    RefreshCw,
    TrendingUp,
    ShoppingCart,
    ChevronLeft,
    ChevronRight,
    ArrowRight,
    ArrowLeft,
    Package
} from 'lucide-react';
import { useNotification } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import { transactionAPI } from '../utils/api';
import PageToolbar from '../components/PageToolbar';
import { displayUpperCase } from '../utils/textUtils';

const TransactionTracking = () => {
    const { showError } = useNotification();
    const { isWarehouse } = useAuth();

    // State for date filtering
    const [dateFilter, setDateFilter] = useState('daily'); // daily, weekly, monthly
    const [currentDate, setCurrentDate] = useState(new Date());
    const [loading, setLoading] = useState(false);

    // State for transactions and sales
    const [balanceTransactions, setBalanceTransactions] = useState([]);
    const [sales, setSales] = useState([]);
    const [carTransfers, setCarTransfers] = useState([]);
    const [summary, setSummary] = useState({
        payments: { count: 0, total: 0 },
        sales: { count: 0, total: 0 },
        transfers: { count: 0 }
    });


    // Auto-refresh state
    const [refreshInterval, setRefreshInterval] = useState(null);

    // Filter transactions to show only paid balances
    const paidBalanceTransactions = balanceTransactions.filter(transaction => transaction.isPayment);

    // Since search is now handled by API, use the data directly
    const filteredBalanceTransactions = paidBalanceTransactions;
    const filteredSales = sales;

    // Calculate summary data - moved directly into useEffect to avoid infinite loops
    useEffect(() => {
        const payments = paidBalanceTransactions;
        const salesData = sales;
        const transfersData = carTransfers;

        const paymentsTotal = payments.reduce((sum, transaction) => sum + transaction.amount, 0);
        const salesTotal = salesData.reduce((sum, sale) => sum + sale.amount, 0);

        setSummary({
            payments: { count: payments.length, total: paymentsTotal },
            sales: { count: salesData.length, total: salesTotal },
            transfers: { count: transfersData.length }
        });
    }, [paidBalanceTransactions, sales, carTransfers]);



    const formatDateShort = (date) => {
        const options = {
            day: '2-digit',
            month: '2-digit'
        };
        return date.toLocaleDateString('tr-TR', options);
    };

    // Navigation functions
    const handlePreviousPeriod = () => {
        const newDate = new Date(currentDate);
        if (dateFilter === 'daily') {
            newDate.setDate(newDate.getDate() - 1);
        } else if (dateFilter === 'weekly') {
            newDate.setDate(newDate.getDate() - 7);
        } else if (dateFilter === 'monthly') {
            newDate.setMonth(newDate.getMonth() - 1);
        }
        setCurrentDate(newDate);
    };

    const handleNextPeriod = () => {
        const newDate = new Date(currentDate);
        if (dateFilter === 'daily') {
            newDate.setDate(newDate.getDate() + 1);
        } else if (dateFilter === 'weekly') {
            newDate.setDate(newDate.getDate() + 7);
        } else if (dateFilter === 'monthly') {
            newDate.setMonth(newDate.getMonth() + 1);
        }
        setCurrentDate(newDate);
    };

    // Fetch data from API - no search filters
    const fetchData = useCallback(async (showErrorToast = true) => {
        try {
            setLoading(true);

            const searchParams = {
                customerSearch: '',
                productSearch: ''
            };

            let data;
            let transfersData;

            switch (dateFilter) {
                case 'daily':
                    data = await transactionAPI.getDailyData(currentDate, searchParams);
                    transfersData = await transactionAPI.getCarTransfers({
                        startDate: new Date(currentDate).setHours(0, 0, 0, 0),
                        endDate: new Date(currentDate).setHours(23, 59, 59, 999),
                        limit: 50
                    });
                    break;
                case 'weekly':
                    data = await transactionAPI.getWeeklyData(currentDate, searchParams);
                    const end = new Date(currentDate);
                    end.setHours(23, 59, 59, 999);
                    const start = new Date(end);
                    start.setDate(start.getDate() - 6);
                    start.setHours(0, 0, 0, 0);
                    transfersData = await transactionAPI.getCarTransfers({
                        startDate: start.toISOString(),
                        endDate: end.toISOString(),
                        limit: 100
                    });
                    break;
                case 'monthly':
                    data = await transactionAPI.getMonthlyData(currentDate, searchParams);
                    const selectedDate = new Date(currentDate);
                    const year = selectedDate.getFullYear();
                    const month = selectedDate.getMonth();
                    const monthStart = new Date(year, month, 1);
                    monthStart.setHours(0, 0, 0, 0);
                    const monthEnd = new Date(year, month + 1, 0);
                    monthEnd.setHours(23, 59, 59, 999);
                    transfersData = await transactionAPI.getCarTransfers({
                        startDate: monthStart.toISOString(),
                        endDate: monthEnd.toISOString(),
                        limit: 200
                    });
                    break;
                default:
                    data = await transactionAPI.getDailyData(currentDate, searchParams);
                    transfersData = await transactionAPI.getCarTransfers({
                        startDate: new Date(currentDate).setHours(0, 0, 0, 0),
                        endDate: new Date(currentDate).setHours(23, 59, 59, 999),
                        limit: 50
                    });
            }

            setBalanceTransactions(data.balanceTransactions);
            setSales(data.sales);
            setCarTransfers(transfersData.data.transfers);
        } catch (error) {
            console.error('Error fetching data:', error);
            if (showErrorToast) {
                showError('Veriler yüklenirken hata oluştu');
            }
            setBalanceTransactions([]);
            setSales([]);
            setCarTransfers([]);
        } finally {
            setLoading(false);
        }
    }, [currentDate, dateFilter, showError]);

    const handleRefresh = () => {
        fetchData();
    };

    // Fetch data when component mounts or date filter changes
    useEffect(() => {
        fetchData();
    }, [fetchData]); // Depend on the callback to avoid stale closures



    // Auto-refresh effect - refresh every 30 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            // Directly call fetchData without depending on the callback reference
            (async () => {
                try {
                    const searchParams = {
                        customerSearch: '',
                        productSearch: ''
                    };

                    let data;
                    let transfersData;

                    switch (dateFilter) {
                        case 'daily':
                            data = await transactionAPI.getDailyData(currentDate, searchParams);
                            transfersData = await transactionAPI.getCarTransfers({
                                startDate: new Date(currentDate).setHours(0, 0, 0, 0),
                                endDate: new Date(currentDate).setHours(23, 59, 59, 999),
                                limit: 50
                            });
                            break;
                        case 'weekly':
                            data = await transactionAPI.getWeeklyData(currentDate, searchParams);
                            const end = new Date(currentDate);
                            end.setHours(23, 59, 59, 999);
                            const start = new Date(end);
                            start.setDate(start.getDate() - 6);
                            start.setHours(0, 0, 0, 0);
                            transfersData = await transactionAPI.getCarTransfers({
                                startDate: start.toISOString(),
                                endDate: end.toISOString(),
                                limit: 100
                            });
                            break;
                        case 'monthly':
                            data = await transactionAPI.getMonthlyData(currentDate, searchParams);
                            const selectedDate = new Date(currentDate);
                            const year = selectedDate.getFullYear();
                            const month = selectedDate.getMonth();
                            const monthStart = new Date(year, month, 1);
                            monthStart.setHours(0, 0, 0, 0);
                            const monthEnd = new Date(year, month + 1, 0);
                            monthEnd.setHours(23, 59, 59, 999);
                            transfersData = await transactionAPI.getCarTransfers({
                                startDate: monthStart.toISOString(),
                                endDate: monthEnd.toISOString(),
                                limit: 200
                            });
                            break;
                        default:
                            data = await transactionAPI.getDailyData(currentDate, searchParams);
                            transfersData = await transactionAPI.getCarTransfers({
                                startDate: new Date(currentDate).setHours(0, 0, 0, 0),
                                endDate: new Date(currentDate).setHours(23, 59, 59, 999),
                                limit: 50
                            });
                    }

                    setBalanceTransactions(data.balanceTransactions);
                    setSales(data.sales);
                    setCarTransfers(transfersData.data.transfers);
                } catch (error) {
                    console.error('Auto-refresh error:', error);
                    // Don't show error toast for auto-refresh
                }
            })();
        }, 30000); // Refresh every 30 seconds

        setRefreshInterval(interval);

        return () => {
            clearInterval(interval);
            setRefreshInterval(null);
        };
    }, [currentDate, dateFilter]); // Only depend on date-related state

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (refreshInterval) {
                clearInterval(refreshInterval);
            }
        };
    }, [refreshInterval]);



    return (
        <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
            <PageToolbar
                right={
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex bg-gray-100 rounded-lg p-1">
                            {[
                                { key: 'daily', label: 'Günlük' },
                                { key: 'weekly', label: 'Haftalık' },
                                { key: 'monthly', label: 'Aylık' }
                            ].map((filter) => (
                                <button
                                    key={filter.key}
                                    onClick={() => setDateFilter(filter.key)}
                                    className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${dateFilter === filter.key
                                            ? 'bg-white text-blue-600 shadow-sm'
                                            : 'text-gray-600 hover:text-gray-900'
                                        }`}
                                >
                                    {filter.label}
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={handleRefresh}
                            disabled={loading}
                            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            title="Yenile"
                        >
                            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                            Yenile
                        </button>
                    </div>
                }
            >
                <div className="flex items-center gap-2">
                    <button
                        onClick={handlePreviousPeriod}
                        className="rounded-lg border border-gray-200 bg-white p-2 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    <DatePicker
                        selected={currentDate}
                        onChange={(date) => {
                            if (date) {
                                setCurrentDate(date);
                            }
                        }}
                        dateFormat={dateFilter === 'daily' ? 'dd/MM/yyyy' : 'MM/yyyy'}
                        locale={tr}
                        className="px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center min-w-[140px]"
                        showMonthYearPicker={dateFilter === 'monthly'}
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                    />
                    <button
                        onClick={handleNextPeriod}
                        className="rounded-lg border border-gray-200 bg-white p-2 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
                    >
                        <ChevronRight className="w-5 h-5" />
                    </button>
                </div>
            </PageToolbar>

            {/* Summary Cards */}
            <div className="bg-white border-b border-gray-200 px-6 py-4">
                <div className="flex flex-wrap gap-6">
                    {/* Payments Card - Hidden for warehouse users */}
                    {!isWarehouse() && (
                        <div className="bg-white border border-gray-200 rounded-lg p-6 flex-1 min-w-[280px]">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">Ödemeler</h3>
                                    <p className="text-sm text-green-600 font-medium mt-1">
                                        {summary.payments.count} işlem
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-2xl font-bold text-gray-900">
                                        {summary.payments.total.toLocaleString('tr-TR', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        })} ₺
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Sales Card - Show only count for warehouse users */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6 flex-1 min-w-[280px]">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">Satışlar</h3>
                                <p className="text-sm text-green-600 font-medium mt-1">
                                    {summary.sales.count} satış
                                </p>
                            </div>
                            <div className="text-right">
                                {!isWarehouse() ? (
                                    <p className="text-2xl font-bold text-gray-900">
                                        {summary.sales.total.toLocaleString('tr-TR', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        })} ₺
                                    </p>
                                ) : (
                                    <div className="text-2xl font-bold text-gray-900">
                                        {summary.sales.count}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Transfers Card */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6 flex-1 min-w-[280px]">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">Araç Transferleri</h3>
                                <p className="text-sm text-purple-600 font-medium mt-1">
                                    {summary.transfers.count} transfer
                                </p>
                            </div>
                            <div className="text-right">
                                <Package className="w-8 h-8 text-purple-600 mx-auto" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex overflow-hidden">
                {/* Left Side - Balance Transactions - Completely hidden for warehouse users */}
                {!isWarehouse() && (
                    <div className="flex-1 bg-white border-r border-gray-200 p-6 overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-gray-900">
                                Müşteri İşlemleri
                            </h2>
                        </div>

                    <div className="space-y-3">
                        {loading ? (
                            <div className="text-center py-8 text-gray-500">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                                <p>İşlemler yükleniyor...</p>
                            </div>
                        ) : filteredBalanceTransactions.length > 0 ? (
                            filteredBalanceTransactions.map((transaction) => (
                                <div
                                    key={transaction.id}
                                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                >
                                    <div className="flex items-center space-x-3">
                                        <div>
                                            <p className="font-medium text-gray-900">{displayUpperCase(transaction.customerName)}</p>
                                            <p className="text-sm text-gray-500">{displayUpperCase(transaction.brand)} - {displayUpperCase(transaction.productName)} - {displayUpperCase(transaction.model)}</p>
                                            <p className="text-sm text-gray-600">{transaction.transactionType}</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className={`font-bold text-green-600`}>
                                            {transaction.transactionType.includes('Ödeme alındı') ? '-' : '+'}
                                            {transaction.amount.toLocaleString('tr-TR', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })} ₺
                                        </p>
                                        <p className="text-sm text-gray-500">{formatDateShort(new Date(transaction.date))}</p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-8 text-gray-500">
                                <TrendingUp className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                                <p>Henüz ödeme alınmış işlem bulunmuyor</p>
                            </div>
                        )}
                    </div>
                    </div>
                )}

                {/* Middle - Sales - Hide amounts for warehouse users */}
                <div className="flex-1 bg-white border-r border-gray-200 p-6 overflow-y-auto">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-gray-900">
                            Ürün Satışları
                        </h2>
                    </div>

                    <div className="space-y-3">
                        {loading ? (
                            <div className="text-center py-8 text-gray-500">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                                <p>Satışlar yükleniyor...</p>
                            </div>
                        ) : filteredSales.length > 0 ? (
                            filteredSales.map((sale) => (
                                <div
                                    key={sale.id}
                                    className="flex items-center justify-between p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                                >
                                    <div className="flex items-center space-x-3">
                                        <div>
                                            <p className="font-medium text-gray-900">{displayUpperCase(sale.productName || sale.customerName)}</p>
                                            <p className="text-sm text-gray-600">{displayUpperCase(sale.salesId)}</p>
                                            {sale.type === 'car' && (
                                                <p className="text-sm text-gray-700 mt-1">
                                                    {`Araç: ${sale.carPlate} • Ürün: ${sale.brand ? sale.brand + ' ' : ''}${sale.productName} • Adet: ${sale.quantity}`}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        {!isWarehouse() ? (
                                            <p className="font-bold text-green-600 flex items-center justify-end">
                                                <span>{sale.amount.toLocaleString('tr-TR', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                })}</span>
                                                <span className="ml-1">₺</span>
                                            </p>
                                        ) : (
                                            <p className="font-bold text-green-600 flex items-center justify-end">
                                                <span>{sale.quantity || 1}</span>
                                                <span className="ml-1">adet</span>
                                            </p>
                                        )}
                                        <p className="text-sm text-gray-500">{formatDateShort(new Date(sale.date))}</p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-8 text-gray-500">
                                <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                                <p>Henüz satış işlemi bulunmuyor</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Side - Car Transfers */}
                <div className="flex-1 bg-white p-6 overflow-y-auto">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-gray-900">
                            Araç Transferleri
                        </h2>
                    </div>

                    <div className="space-y-3">
                        {loading ? (
                            <div className="text-center py-8 text-gray-500">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                                <p>Transferler yükleniyor...</p>
                            </div>
                        ) : carTransfers.length > 0 ? (
                            carTransfers.map((transfer) => {
                                const isReturn = transfer.notes && transfer.notes.includes('azaltma');
                                return (
                                    <div
                                        key={transfer.id}
                                        className={`flex items-center justify-between p-4 rounded-lg hover:bg-opacity-80 transition-colors ${isReturn
                                                ? 'bg-orange-50 hover:bg-orange-100'
                                                : 'bg-purple-50 hover:bg-purple-100'
                                            }`}
                                    >
                                        <div className="flex items-center space-x-3">
                                            {isReturn ? (
                                                <ArrowLeft className="w-5 h-5 text-orange-600" />
                                            ) : (
                                                <ArrowRight className="w-5 h-5 text-purple-600" />
                                            )}
                                            <div>
                                                <p className="font-medium text-gray-900">{transfer.carPlate}</p>
                                                <p className="text-sm text-gray-500">{transfer.productBrand} {transfer.productName}</p>
                                                <p className={`text-sm font-medium ${isReturn ? 'text-orange-600' : 'text-purple-600'
                                                    }`}>
                                                    {isReturn ? 'Stoğa' : 'Araca'} - {transfer.quantity} adet
                                                </p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-sm text-gray-500">{formatDateShort(new Date(transfer.transferDate))}</p>
                                            {transfer.notes && (
                                                <p className="text-xs text-gray-400 mt-1 max-w-32 truncate" title={transfer.notes}>
                                                    {transfer.notes}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="text-center py-8 text-gray-500">
                                <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                                <p>Henüz araç transferi bulunmuyor</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TransactionTracking;
